import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Tag } from "lucide-react";

export const BLOG_POSTS = [
  {
    slug: "what-is-satta-matka",
    title: "What is Satta Matka? Complete Guide for Beginners (2026)",
    metaTitle: "What is Satta Matka? Complete Beginner Guide 2026 – Play Online Satta Matka",
    metaDesc: "Learn what Satta Matka is, how it works online, types of games (Single, Jodi, Patti), popular markets like Kalyan and Milan, and how to play safely in 2026.",
    category: "Beginners Guide",
    date: "July 5, 2026",
    readTime: "7 min read",
    excerpt: "Everything you need to know about Satta Matka — how it started, how it works online, game types, popular markets, and how to get started safely.",
    sections: [
      {
        h2: "What is Satta Matka?",
        body: "Satta Matka is a number-based game of chance that originated in India in the 1960s. Originally it involved betting on the opening and closing rates of cotton traded on the New York Cotton Exchange. Over decades it evolved into a pure number game where players bet on randomly declared numbers.\n\nToday, Online Satta Matka is fully digital. Players choose numbers and game types, place bets using their wallet balance, and receive payouts when the market result matches their selection. Leading markets like Kalyan Matka, Milan Day, Milan Night, Rajdhani Day, Rajdhani Night and Main Bazar operate daily with fixed timings.",
      },
      {
        h2: "How Does Online Satta Matka Work?",
        body: "Each Matka market has an Open session and a Close session. The result is a combination of Open Pana (a 3-digit number) and Close Pana, from which Open Digit and Close Digit are derived. Together they form the Jodi (a 2-digit number like 45 or 73).\n\nFor example:\n• Open Pana: 123 → Open Digit: 1+2+3 = 6\n• Close Pana: 458 → Close Digit: 4+5+8 = 17 → 7\n• Jodi: 67\n\nPlayers can bet on any of these: the Open/Close Pana, the Open/Close Digit, or the full Jodi.",
      },
      {
        h2: "Types of Satta Matka Games",
        body: "Play Online Satta Matka offers 8 game types:\n\n• Single Digit (Ank) — Bet on a single digit 0–9. Win rate: 9× your bet.\n• Jodi — Bet on a 2-digit pair 00–99. Win rate: 90× your bet.\n• Red Bracket — A special variant of single-digit betting with different odds.\n• Single Patti (SP) — A 3-digit number with all different digits (e.g. 123). Win rate: 150×.\n• Double Patti (DP) — A 3-digit number with two same digits (e.g. 112). Win rate: 270×.\n• Triple Patti (TP) — A 3-digit number with all same digits (e.g. 111, 222). Win rate: 800×.\n• Half Sangam — Combination of a Pana and a Digit from opposite sessions. Win rate: 1500×.\n• Full Sangam — Combination of both Open and Close Panas. Win rate: 11,250×.",
      },
      {
        h2: "Popular Satta Matka Markets in India",
        body: "These are the most popular markets played daily on Play Online Satta Matka:\n\n• Kalyan Matka — Open at 3:45 PM, Close at 5:45 PM (Mon–Sat)\n• Milan Day — Open at 1:30 PM, Close at 2:30 PM\n• Milan Night — Open at 9:00 PM, Close at 10:00 PM\n• Rajdhani Day — Open at 3:15 PM, Close at 5:15 PM\n• Rajdhani Night — Open at 9:30 PM, Close at 11:30 PM\n• Main Bazar — Open at 9:30 PM, Close at 12:00 AM\n\nStarline markets also run throughout the day with time-slot results every hour.",
      },
      {
        h2: "Is Satta Matka Legal in India?",
        body: "The legality of Satta Matka varies by state in India. It is the user's responsibility to check whether playing skill/chance-based games is permitted in their location. Play Online Satta Matka is a platform for entertainment. Players must be 18 years or above. We encourage responsible play — only play with money you can afford to lose.",
      },
    ],
  },
  {
    slug: "how-to-play-kalyan-matka-online",
    title: "How to Play Kalyan Matka Online – Full Step by Step Guide (2026)",
    metaTitle: "How to Play Kalyan Matka Online – Step by Step 2026 – Play Online Satta Matka",
    metaDesc: "Learn how to play Kalyan Matka online step by step. Register, deposit, choose game type, place bets and check results on Play Online Satta Matka platform.",
    category: "How to Play",
    date: "July 3, 2026",
    readTime: "6 min read",
    excerpt: "Step-by-step guide to playing Kalyan Matka online — from registering your account and adding money to placing bets and checking results.",
    sections: [
      {
        h2: "What is Kalyan Matka?",
        body: "Kalyan Matka is the most popular Satta Matka market in India. It runs 6 days a week (Monday to Saturday) with Open at 3:45 PM and Close at 5:45 PM IST. It was originally started by Ratan Khatri in the 1970s and remains the benchmark market for all Matka players.\n\nKalyan Matka offers all standard game types — Single Digit, Jodi, Single Patti, Double Patti, Triple Patti, Half Sangam and Full Sangam.",
      },
      {
        h2: "Step 1 — Register on Play Online Satta Matka",
        body: "Visit playonlinesattamatka.com and tap Register. Enter your 10-digit mobile number and the OTP sent to your phone. Set a username if prompted. Your account is ready in under 30 seconds — no documents or email needed.",
      },
      {
        h2: "Step 2 — Add Money to Your Wallet",
        body: "Tap Wallet → Add Money. Enter the amount (minimum shown on screen). Scan the UPI QR code or copy the UPI ID. Pay using PhonePe, Google Pay, Paytm, or any UPI app on your phone. Upload the payment screenshot. Your wallet is credited after verification — usually within minutes during active hours.",
      },
      {
        h2: "Step 3 — Open Kalyan Market and Choose Game Type",
        body: "From the main screen, tap Kalyan Matka. Check that the market is currently Open (green status). Choose your game type:\n• Single Digit — pick a number 0–9\n• Jodi — pick two digits (e.g. 45)\n• Single Patti — pick a valid 3-digit SP number\n• Double or Triple Patti — pick accordingly\n\nFor beginners, Single Digit is the easiest to understand. Your win is 9 times your bet.",
      },
      {
        h2: "Step 4 — Place Your Bet",
        body: "Enter the number you want to bet on, enter your bet amount (e.g. ₹50), and tap Submit Bid. You will see your bid in My Bids. The market will close at 5:45 PM. After results are declared, winners receive their payout automatically to their wallet.",
      },
      {
        h2: "Step 5 — Check Results and Withdraw",
        body: "After close time, go to Charts or your Bids page to see the result. If you won, the payout is added to your wallet balance. To withdraw, go to Wallet → Withdraw, enter the amount and your UPI/bank details. Withdrawals are processed the same day during business hours.",
      },
      {
        h2: "Kalyan Matka Tips for Beginners",
        body: "• Always check market timing before placing a bid — bids after close time are not valid.\n• Start with Single Digit bets while learning.\n• Never bet more than you can afford to lose.\n• Check the Jodi Chart and Panel Chart for historical results — available under Charts.\n• Play responsibly. Set a daily limit for yourself.",
      },
    ],
  },
  {
    slug: "satta-matka-game-types-explained",
    title: "Satta Matka Game Types Explained – Single, Jodi, Patti & More",
    metaTitle: "Satta Matka Game Types Explained – Single, Jodi, Patti Guide – Play Online Satta Matka",
    metaDesc: "Complete explanation of all Satta Matka game types — Single Digit, Jodi, Red Bracket, Single Patti, Double Patti, Triple Patti, Half Sangam and Full Sangam with win rates.",
    category: "Game Guide",
    date: "July 1, 2026",
    readTime: "8 min read",
    excerpt: "A complete breakdown of every Satta Matka game type available — how each works, what numbers to pick, and the win rates for Single, Jodi, SP, DP, TP, and Sangam.",
    sections: [
      {
        h2: "Overview of Satta Matka Game Types",
        body: "Play Online Satta Matka offers 8 different game types, each with different number selection and win rates. The key is understanding the result format: each market declares an Open Pana, Open Digit, Close Pana, Close Digit, and Jodi. Each game type uses a different part of this result.",
      },
      {
        h2: "Single Digit (Ank) — Win 9×",
        body: "Single Digit (also called Ank) means betting on one number from 0 to 9. You bet on the Open Digit, the Close Digit, or both.\n\nExample: You bet ₹100 on Open 7. If the Open Digit declared is 7, you win ₹900 (9 times your bet). Easy to play, lower payout, higher hit chance.\n\nBulk Single lets you bet different amounts on multiple digits at once.",
      },
      {
        h2: "Jodi — Win 90×",
        body: "Jodi means betting on a 2-digit pair from 00 to 99. The Jodi is formed by combining the Open Digit and Close Digit of a market result.\n\nExample: If Open Digit is 4 and Close Digit is 7, the Jodi is 47. If you bet ₹10 on Jodi 47, you win ₹900 (90 times your bet). Jodi is the most popular game type in Matka.",
      },
      {
        h2: "Single Patti (SP) — Win 150×",
        body: "Single Patti (SP) is a 3-digit number where all three digits are different from each other (e.g. 123, 456, 789). There are 120 valid SP numbers in a full set.\n\nYou bet on either the Open Pana or the Close Pana being an SP number you chose. Win rate: 150 times your bet.\n\nSP Bulk lets you bet on all SP numbers in one go. SP Motor uses a pattern-based selection.",
      },
      {
        h2: "Double Patti (DP) — Win 270×",
        body: "Double Patti (DP) is a 3-digit number where exactly two digits are the same (e.g. 112, 224, 455). There are 90 valid DP numbers.\n\nHigher win rate than SP (270×) because fewer DP numbers appear as results compared to SP numbers. A ₹10 bet on a winning DP number returns ₹2,700.",
      },
      {
        h2: "Triple Patti (TP) — Win 800×",
        body: "Triple Patti is a 3-digit number where all three digits are the same — only 10 possible numbers (000, 111, 222, 333, 444, 555, 666, 777, 888, 999).\n\nVery rare result, which is why the win rate is the highest at 800 times your bet. A ₹10 bet returns ₹8,000 if you pick the correct TP.",
      },
      {
        h2: "Half Sangam — Win 1500×",
        body: "Half Sangam combines a Pana from one session and a Digit from the other session. For example: Open Pana 123 + Close Digit 7 = Half Sangam (123-7). Win rate: 1500× your bet.",
      },
      {
        h2: "Full Sangam — Win 11,250×",
        body: "Full Sangam is the highest-paying game type. You bet on the exact Open Pana AND Close Pana combination. Example: 123-456 (Open Pana 123, Close Pana 456). Win rate: 11,250 times your bet. Extremely rare but the biggest single win possible in Matka.",
      },
    ],
  },
  {
    slug: "how-to-read-matka-charts",
    title: "How to Read Satta Matka Charts – Jodi Chart and Panel Chart Guide",
    metaTitle: "How to Read Satta Matka Charts – Jodi & Panel Chart Guide – Play Online Satta Matka",
    metaDesc: "Learn how to read Satta Matka Jodi charts and Panel charts. Understand historical results, patterns and how to use charts to make better game decisions.",
    category: "Charts & Results",
    date: "June 28, 2026",
    readTime: "5 min read",
    excerpt: "A simple guide to reading and using Satta Matka charts — what Jodi charts and Panel charts show, how to find patterns, and where to access charts on Play Online Satta Matka.",
    sections: [
      {
        h2: "What are Satta Matka Charts?",
        body: "Matka charts are historical records of all past results for a particular market. They are presented in table form with dates going back weeks, months, or years. There are two main types of charts:\n\n1. Jodi Chart — Shows the 2-digit Jodi result for each day\n2. Panel Chart (Pana Chart) — Shows the Open Pana and Close Pana for each day\n\nCharts are used by experienced players to spot patterns, recurring numbers, and gaps in results.",
      },
      {
        h2: "How to Read a Jodi Chart",
        body: "The Jodi Chart is a simple grid. Each row represents one week. Each column is a day of the week (Mon–Sat or Mon–Sun depending on the market). The number in each cell is the 2-digit Jodi result for that date.\n\nFor example, Kalyan Matka Jodi Chart might show:\n• Monday: 47\n• Tuesday: 82\n• Wednesday: 31\n\nPlayers look for which Jodis repeat, which pairs of digits appear together, or how long a particular digit hasn't appeared in a session.",
      },
      {
        h2: "How to Read a Panel Chart (Pana Chart)",
        body: "The Panel Chart is more detailed. Each date cell shows three values:\n• Top number: Open Pana (3-digit)\n• Middle number: Jodi (2-digit)\n• Bottom number: Close Pana (3-digit)\n\nFor example: 123 | 67 | 458 means Open Pana was 123, the result digit was 6, Close Pana was 458, Close digit was 7, and the Jodi was 67.\n\nThis allows players to see not just which Jodis appeared, but also which Panas were declared — helping with SP, DP, and TP game type decisions.",
      },
      {
        h2: "How to Access Charts on Play Online Satta Matka",
        body: "From the home screen, tap the Charts button or any market card's Jodi Chart / Pana Chart link. You can also go to the Charts page from the navigation menu.\n\nAvailable charts include:\n• Kalyan Matka Jodi Chart\n• Kalyan Matka Panel Chart\n• Milan Day/Night Charts\n• Rajdhani Day/Night Charts\n• Main Bazar Charts\n• Starline Charts\n\nCharts are updated automatically after every result.",
      },
      {
        h2: "Using Charts Wisely",
        body: "Charts show historical data — they do not predict future results. Matka is a game of chance. No pattern guarantees a win. Use charts as a reference tool, not as a guaranteed strategy. Always set a personal limit and play responsibly.",
      },
    ],
  },
  {
    slug: "responsible-matka-play-tips",
    title: "5 Important Tips for Safe and Responsible Online Matka Play",
    metaTitle: "5 Tips for Safe & Responsible Online Matka Play – Play Online Satta Matka",
    metaDesc: "Essential tips for playing Online Satta Matka safely and responsibly. Set limits, protect your account, avoid common mistakes and play within your means.",
    category: "Responsible Play",
    date: "June 25, 2026",
    readTime: "4 min read",
    excerpt: "5 practical tips to help you enjoy Matka games safely — from setting spending limits to protecting your account and understanding the risks involved.",
    sections: [
      {
        h2: "Tip 1 — Set a Daily Betting Limit and Stick to It",
        body: "Before you start playing, decide the maximum amount you are willing to spend today. This is your limit. Do not cross it — even if you feel like you can win it back. Losses can multiply quickly in betting games. Treating Matka as entertainment with a fixed budget protects you from financial stress.",
      },
      {
        h2: "Tip 2 — Never Bet With Borrowed Money",
        body: "Only play with money you can genuinely afford to lose. Never borrow money to fund a Matka wallet. Never use rent, grocery, EMI, or emergency funds. If losing today's deposit would cause real stress or hardship, do not deposit that amount. Your financial safety is always more important than any bet.",
      },
      {
        h2: "Tip 3 — Protect Your Account and OTP",
        body: "Never share your OTP with anyone — not even if they claim to be from support. Your OTP is your account key. Never share your phone with others while logged in. Log out from devices you don't use. Play Online Satta Matka will never call you asking for OTP, password, or wallet screenshot. Report suspicious contacts immediately.",
      },
      {
        h2: "Tip 4 — Do Not Chase Losses",
        body: "One of the most dangerous mistakes in any betting game is chasing a loss — placing bigger bets to recover what you lost. This rarely works and almost always leads to bigger losses. If you hit your daily limit or lose a session, stop for the day. Reset tomorrow. Discipline is the single biggest factor in long-term responsible play.",
      },
      {
        h2: "Tip 5 — Play Only if You Are 18+ and It Is Legal in Your Area",
        body: "Play Online Satta Matka is strictly for players aged 18 years or above. Minors are not permitted. It is also your responsibility to verify that playing this type of game is legally permitted in your state or location. If you have any doubt, consult local regulations before playing.",
      },
      {
        h2: "When to Seek Help",
        body: "If Matka play is affecting your relationships, finances, work, or mental health, take a break and seek help. You can contact support@playonlinesattamatka.com to request account restrictions. Responsible gaming resources are also available from Indian mental health helplines.",
      },
    ],
  },
];

function BlogPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const prev = document.title;
    document.title = "Satta Matka Blog – Tips, Guides & Game Strategies | Play Online Satta Matka";
    const desc = document.querySelector("meta[name='description']");
    const prevDesc = desc?.getAttribute("content") || "";
    desc?.setAttribute("content", "Read the latest Satta Matka guides, tips, game type explanations, chart reading tutorials and responsible play advice on Play Online Satta Matka blog.");
    return () => {
      document.title = prev;
      if (desc) desc.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <main style={pg}>
      <header style={topbar}>
        <button type="button" style={backBtn} onClick={() => navigate(-1)}>
          <ArrowLeft size={18} />
        </button>
        <span style={brand}>
          <span style={{ color: "#f97316" }}>PLAY ONLINE</span>
          <span style={{ color: "#16a34a" }}> SATTA MATKA</span>
        </span>
      </header>

      <section style={heroSection}>
        <p style={eyebrow}>Our Blog</p>
        <h1 style={heroH1}>Satta Matka Tips, Guides & Strategies</h1>
        <p style={heroPara}>Learn how Matka works, how to read charts, understand game types and play responsibly. Updated regularly with expert guides.</p>
      </section>

      <section style={listSection}>
        <div style={grid}>
          {BLOG_POSTS.map((post) => (
            <article key={post.slug} style={card} onClick={() => navigate(`/blog/${post.slug}`)}>
              <div style={cardTop}>
                <span style={catBadge}>{post.category}</span>
                <span style={readTimeBadge}><Clock size={11} /> {post.readTime}</span>
              </div>
              <h2 style={cardTitle}>{post.title}</h2>
              <p style={cardExcerpt}>{post.excerpt}</p>
              <div style={cardFoot}>
                <span style={cardDate}>{post.date}</span>
                <span style={readMore}>Read more →</span>
              </div>
            </article>
          ))}
        </div>
      </section>

      <footer style={ftr}>
        <p style={ftrNote}>© 2026 Play Online Satta Matka. All Rights Reserved. | 18+ Only | Play Responsibly</p>
        <div style={ftrLinks}>
          {[["Home", "/"], ["Charts", "/charts"], ["FAQ", "/faq"], ["How to Play", "/how-to-play"], ["About Us", "/about-us"], ["Contact Us", "/contact-us"]].map(([l, p]) => (
            <button key={p} style={ftrLnk} onClick={() => navigate(p)}>{l}</button>
          ))}
        </div>
      </footer>
    </main>
  );
}

const pg          = { minHeight: "100vh", background: "#f9fafb", fontFamily: "Arial, Helvetica, sans-serif", color: "#111827" };
const topbar      = { display: "flex", alignItems: "center", gap: 14, padding: "14px 20px", background: "#fff", borderBottom: "1px solid #e5e7eb", position: "sticky", top: 0, zIndex: 10 };
const backBtn     = { width: 36, height: 36, border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", display: "grid", placeItems: "center", cursor: "pointer" };
const brand       = { fontWeight: 900, fontSize: 18, fontFamily: "'Arial Black', sans-serif" };
const heroSection = { background: "#312e81", color: "#fff", padding: "48px 24px 40px", textAlign: "center" };
const eyebrow     = { margin: "0 0 10px", fontSize: 13, fontWeight: 800, color: "#fbbf24", textTransform: "uppercase", letterSpacing: "0.1em" };
const heroH1      = { margin: "0 0 14px", fontSize: "clamp(24px,4vw,36px)", fontWeight: 900 };
const heroPara    = { margin: 0, fontSize: 16, color: "rgba(255,255,255,.8)", maxWidth: 620, marginLeft: "auto", marginRight: "auto" };
const listSection = { maxWidth: 1100, margin: "0 auto", padding: "40px 24px 60px" };
const grid        = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 20 };
const card        = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "20px 20px 16px", cursor: "pointer", display: "flex", flexDirection: "column", gap: 10, boxShadow: "0 2px 8px rgba(0,0,0,.04)" };
const cardTop     = { display: "flex", alignItems: "center", gap: 10 };
const catBadge    = { background: "#eff6ff", color: "#2563eb", fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 999 };
const readTimeBadge = { display: "flex", alignItems: "center", gap: 4, color: "#6b7280", fontSize: 11 };
const cardTitle   = { margin: 0, fontSize: 16, fontWeight: 800, color: "#111827", lineHeight: 1.4 };
const cardExcerpt = { margin: 0, fontSize: 13, color: "#6b7280", lineHeight: 1.6, flex: 1 };
const cardFoot    = { display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 8, borderTop: "1px solid #f3f4f6" };
const cardDate    = { fontSize: 12, color: "#9ca3af" };
const readMore    = { fontSize: 13, fontWeight: 700, color: "#f97316" };
const ftr         = { background: "#111827", color: "#fff", padding: "28px 24px", textAlign: "center" };
const ftrNote     = { fontSize: 12, color: "#9ca3af", marginBottom: 14 };
const ftrLinks    = { display: "flex", flexWrap: "wrap", gap: "6px 16px", justifyContent: "center" };
const ftrLnk      = { border: 0, background: "transparent", color: "#d1d5db", fontSize: 13, fontWeight: 600, cursor: "pointer" };

export default BlogPage;
